/**
 * Exclusion-aware usable-area geometry for the generation test fit.
 *
 * Operates purely on pre-localized metre-plane rings (the same rotated,
 * parcel-centred frame produced by `generationLayoutEnvelopeForParcel`), so it
 * stays free of lat/lng transforms and can be unit-tested with synthetic
 * geometry. All clipping is fail-closed: a `null` return means the exclusion
 * geometry could not be applied and the caller must refuse a `fits` verdict
 * rather than silently ignore the zones.
 */
import polygonClipping from 'polygon-clipping';

export const SQUARE_METRES_PER_ACRE = 4_046.8564224;

export interface LocalPointM {
  xM: number;
  yM: number;
}

/** Close an open metre-plane ring into polygon-clipping's pair format. */
export function closedRingM(points: readonly LocalPointM[]): Array<[number, number]> {
  const ring = points.map(point => [point.xM, point.yM] as [number, number]);
  if (ring.length > 0) {
    const first = ring[0]!;
    const last = ring.at(-1)!;
    if (first[0] !== last[0] || first[1] !== last[1]) ring.push([...first]);
  }
  return ring;
}

export function ringAreaM2(ring: readonly (readonly number[])[]): number {
  let twiceArea = 0;
  for (let index = 0; index + 1 < ring.length; index += 1) {
    const current = ring[index]!;
    const next = ring[index + 1]!;
    twiceArea += current[0]! * next[1]! - next[0]! * current[1]!;
  }
  return Math.abs(twiceArea) / 2;
}

export function multiPolygonAreaM2(multiPolygon: polygonClipping.MultiPolygon): number {
  return multiPolygon.reduce((total, polygon) => {
    if (polygon.length === 0) return total;
    const outer = ringAreaM2(polygon[0]!);
    const holes = polygon.slice(1).reduce((sum, ring) => sum + ringAreaM2(ring), 0);
    return total + Math.max(0, outer - holes);
  }, 0);
}

export interface LocalExclusionGeometry {
  /** All exclusion rings unioned, clipped to the parcel polygon. */
  clippedUnion: polygonClipping.MultiPolygon;
  /** Parcel minus exclusions. */
  usableMultiPolygon: polygonClipping.MultiPolygon;
  parcelAcres: number;
  excludedAcres: number;
  usableAcres: number;
}

/**
 * Subtract exclusion rings from the parcel polygon in the local metre plane.
 * Returns `null` when polygon-clipping rejects the topology; callers must
 * treat that as "screening unavailable", never as "no exclusions".
 */
export function localExclusionGeometry(
  exclusionRings: ReadonlyArray<readonly LocalPointM[]>,
  parcelPolygonM: readonly LocalPointM[],
): LocalExclusionGeometry | null {
  try {
    const parcelGeometry = [closedRingM(parcelPolygonM)] as polygonClipping.Polygon;
    const parcelAcres = multiPolygonAreaM2([parcelGeometry]) / SQUARE_METRES_PER_ACRE;
    const rings = exclusionRings
      .map(ring => closedRingM(ring))
      .filter(ring => ring.length >= 4);
    if (rings.length === 0) {
      return {
        clippedUnion: [],
        usableMultiPolygon: [parcelGeometry],
        parcelAcres,
        excludedAcres: 0,
        usableAcres: parcelAcres,
      };
    }
    let union: polygonClipping.MultiPolygon = polygonClipping.union(
      [rings[0]!] as polygonClipping.Polygon,
    );
    for (let index = 1; index < rings.length; index += 1) {
      union = polygonClipping.union(union, [rings[index]!] as polygonClipping.Polygon);
    }
    const clippedUnion = polygonClipping.intersection(union, parcelGeometry);
    const usableMultiPolygon = polygonClipping.difference(parcelGeometry, union);
    const excludedAcres = multiPolygonAreaM2(clippedUnion) / SQUARE_METRES_PER_ACRE;
    return {
      clippedUnion,
      usableMultiPolygon,
      parcelAcres,
      excludedAcres,
      usableAcres: Math.max(0, parcelAcres - excludedAcres),
    };
  } catch {
    return null;
  }
}

/**
 * Area of one metre-plane polygon that overlaps the given geometry, in acres.
 * Returns `null` on clipping failure (fail closed).
 */
export function polygonOverlapAcres(
  polygonM: readonly LocalPointM[],
  geometry: polygonClipping.MultiPolygon,
): number | null {
  if (geometry.length === 0) return 0;
  try {
    const overlap = polygonClipping.intersection(
      [closedRingM(polygonM)] as polygonClipping.Polygon,
      geometry,
    );
    return multiPolygonAreaM2(overlap) / SQUARE_METRES_PER_ACRE;
  } catch {
    return null;
  }
}
